import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import {
  getAllMenu,
  getAllFlatMenu,
  getSingleMenu,
  createMenu,
  updateMenu,
  deleteMenu,
  getMenuManagementOverview,
} from "@/api/new-api/staff-management/menu-manage/menu";

// Thunks
export const fetchAllMenu = createAsyncThunk(
  "menu/fetchAll",
  async (_, { rejectWithValue }) => {
    const res = await getAllMenu();
    if (res.status === "success") return res.data.menuItems;
    return rejectWithValue(res.message);
  }
);

export const fetchAllFlatMenu = createAsyncThunk(
  "menu/fetchAllFlat",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllFlatMenu(restaurant_id);
    if (res.status === "success") return res.data.menu_items;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleMenu = createAsyncThunk(
  "menu/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleMenu(id);
    if (res.status === "success") return res.data.menuItem;
    return rejectWithValue(res.message);
  }
);

export const createNewMenu = createAsyncThunk(
  "menu/create",
  async (data, { rejectWithValue }) => {
    const res = await createMenu(data);
    if (res.status === "success") return res.data.menuItem;
    return rejectWithValue(res.message);
  }
);

export const updateExistingMenu = createAsyncThunk(
  "menu/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateMenu(id, data);
    if (res.status === "success") return res.data.menuItem;
    return rejectWithValue(res.message);
  }
);

export const deleteMenuById = createAsyncThunk(
  "menu/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteMenu(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

export const fetchMenuManagementOverview = createAsyncThunk(
  "menu/fetchMenuManagementOverview",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getMenuManagementOverview(restaurant_id);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
  }
);

// Initial State
const initialState = {
  menus: [],
  flatMenus: [],
  singleMenu: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,

  // Menu management overview
  menuOverview: null,
  menuOverviewLoading: false,
};

// Slice
const menuSlice = createSlice({
  name: "menu",
  initialState,
  reducers: {
    clearMenuMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllMenu.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllMenu.fulfilled, (state, action) => {
        state.getLoading = false;
        state.menus = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllMenu.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch All Flat
      .addCase(fetchAllFlatMenu.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllFlatMenu.fulfilled, (state, action) => {
        state.getLoading = false;
        state.flatMenus = action.payload.map((item) => ({
          ...item,
          unique_id: uuidv4(),
        }));
        state.requestStatus = true;
      })

      .addCase(fetchAllFlatMenu.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleMenu.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleMenu.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleMenu = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleMenu.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewMenu.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewMenu.fulfilled, (state, action) => {
        state.createLoading = false;
        state.menus.push(action.payload);
        state.success = "Menu created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewMenu.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingMenu.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingMenu.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.menus = state.menus.map((menu) =>
          menu.id === action.payload.id ? action.payload : menu
        );
        state.success = "Menu updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingMenu.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteMenuById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteMenuById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.menus = state.menus.filter((menu) => menu.id !== action.payload);
        state.success = "Menu deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteMenuById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Menu Management Overview
      .addCase(fetchMenuManagementOverview.pending, (state) => {
        state.menuOverviewLoading = true;
        state.error = null;
      })
      .addCase(fetchMenuManagementOverview.fulfilled, (state, action) => {
        state.menuOverviewLoading = false;
        state.menuOverview = action.payload;
      })
      .addCase(fetchMenuManagementOverview.rejected, (state, action) => {
        state.menuOverviewLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearMenuMessages } = menuSlice.actions;
export default menuSlice.reducer;
